import {
  createDefaultWorkOrderFilters,
  formatDateParam,
  normalizeWorkOrderFilterValue,
  type FilterDisplayMode,
  type WorkOrderFilterValue,
} from "../_shared/_logic/work-order-filters"
import { replaceUrlWithoutRoutePublish } from "../../../../../_shared/_logic/routes-root-silent-url-replace"

type WorkOrderTableUrlState = {
  filters: WorkOrderFilterValue
  filterMode: FilterDisplayMode
}

const normalizeId = (value: string | null | undefined) =>
  (value ?? "kehrblatt-work-orders").trim().replace(/[^a-zA-Z0-9_-]+/g, "_") ||
  "kehrblatt-work-orders"

export const workOrdersTableUrlStateId = (organizationId: string | null | undefined) =>
  normalizeId(organizationId ? `kehrblatt-work-orders-${organizationId}` : "kehrblatt-work-orders")

const key = (stateId: string, name: string) => `table_${stateId}_filter_${name}`

const parseJsonStringArray = (value: string | null): string[] => {
  if (!value) return []

  try {
    const parsed = JSON.parse(value)

    if (Array.isArray(parsed)) {
      return parsed.map((entry) => `${entry ?? ""}`.trim()).filter(Boolean)
    }
  } catch {
    // Fall through to comma-separated legacy/fallback parsing.
  }

  return value
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
}

const parseDate = (value: string | null): Date | null => {
  if (!value) return null

  const parsed = new Date(`${value}T12:00:00`)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const setString = (params: URLSearchParams, stateId: string, name: string, value: string) => {
  const normalized = value.trim()

  if (normalized) {
    params.set(key(stateId, name), normalized)
  } else {
    params.delete(key(stateId, name))
  }
}

const setArray = (params: URLSearchParams, stateId: string, name: string, value: string[]) => {
  const normalized = value.map((entry) => entry.trim()).filter(Boolean)

  if (normalized.length) {
    params.set(key(stateId, name), JSON.stringify(normalized))
  } else {
    params.delete(key(stateId, name))
  }
}

const replaceUrl = (params: URLSearchParams) => {
  if (typeof window === "undefined") return

  const nextSearch = params.toString()
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash ?? ""}`
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash ?? ""}`

  if (nextUrl !== currentUrl) {
    replaceUrlWithoutRoutePublish(nextUrl)
  }
}

export const readWorkOrdersTableUrlState = (
  organizationId: string | null | undefined,
): WorkOrderTableUrlState => {
  const defaults = createDefaultWorkOrderFilters()

  if (typeof window === "undefined") return { filters: defaults, filterMode: "compact" }

  const stateId = workOrdersTableUrlStateId(organizationId)
  const params = new URLSearchParams(window.location.search || "")
  const mode = params.get(key(stateId, "mode")) === "detailed" ? "detailed" : "compact"
  const filters = normalizeWorkOrderFilterValue({
    searchQuery: params.get(key(stateId, "search")) ?? defaults.searchQuery,
    orderTypeIds: parseJsonStringArray(params.get(key(stateId, "order_types"))),
    employeeIds: parseJsonStringArray(params.get(key(stateId, "employees"))),
    workOrderStates: parseJsonStringArray(
      params.get(key(stateId, "states")),
    ) as WorkOrderFilterValue["workOrderStates"],
    actionRequiredState: (params.get(key(stateId, "action_required_state")) ??
      defaults.actionRequiredState) as WorkOrderFilterValue["actionRequiredState"],
    appointmentDateRange: {
      start: parseDate(params.get(key(stateId, "appointment_start"))),
      end: parseDate(params.get(key(stateId, "appointment_end"))),
    },
    dueDateRange: {
      start: parseDate(params.get(key(stateId, "due_start"))),
      end: parseDate(params.get(key(stateId, "due_end"))),
    },
    archiveScope: (params.get(key(stateId, "archive_scope")) ??
      defaults.archiveScope) as ArchiveScope,
  })

  return { filters, filterMode: mode }
}

export const writeWorkOrdersTableUrlState = (
  organizationId: string | null | undefined,
  state: WorkOrderTableUrlState,
) => {
  if (typeof window === "undefined") return

  const stateId = workOrdersTableUrlStateId(organizationId)
  const params = new URLSearchParams(window.location.search || "")
  const defaults = createDefaultWorkOrderFilters()
  const filters = normalizeWorkOrderFilterValue(state.filters)

  setString(params, stateId, "search", filters.searchQuery)
  setArray(params, stateId, "order_types", filters.orderTypeIds)
  setArray(params, stateId, "employees", filters.employeeIds)
  setArray(params, stateId, "states", filters.workOrderStates)
  setString(params, stateId, "action_required_state", filters.actionRequiredState)
  setString(
    params,
    stateId,
    "appointment_start",
    formatDateParam(filters.appointmentDateRange.start) ?? "",
  )
  setString(
    params,
    stateId,
    "appointment_end",
    formatDateParam(filters.appointmentDateRange.end) ?? "",
  )
  setString(params, stateId, "due_start", formatDateParam(filters.dueDateRange.start) ?? "")
  setString(params, stateId, "due_end", formatDateParam(filters.dueDateRange.end) ?? "")
  setString(
    params,
    stateId,
    "archive_scope",
    filters.archiveScope === defaults.archiveScope ? "" : filters.archiveScope,
  )

  if (state.filterMode === "detailed") {
    params.set(key(stateId, "mode"), "detailed")
  } else {
    params.delete(key(stateId, "mode"))
  }

  replaceUrl(params)
}
